import { withTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { isTablet } from 'react-device-detect';
import { connect } from 'react-redux';
import compose from 'utils/compose';
import { safeRefIn, treatEnterAsTab } from 'utils/utils';
import { v4 as uuidv4 } from 'uuid';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesActions, categoriesSelectors, categoriesTypes } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsSelectors } from 'app-components/flux/transactions';
import { getLogger } from 'app-components/utils/core';

import Typography from '@mui/material/Typography';
// import CoaUsageTypeIcon from 'components/CoaUsageTypeIcon';
import NestedQMenu from 'components/NestedQMenu';
import QIconButton from 'components/QIconButton';
import ZeroStateView from 'components/ZeroStateView';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ErrorIcon from '@mui/icons-material/Error';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputBase from '@mui/material/InputBase';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';

import { businessSelectors } from 'app-components/flux/business';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { coaIncludedInList } from '../helpers';

const log = getLogger('components/QuickenControls/CategoryFieldEditable/index.js');

const NONE_COA_NODE = {
  id: 'NONE',
  coa: { type: 'NONE', id: '0' },
  name: 'None',
  fullPathName: '<none>',
  fullPathNameWithoutUsageType: '<none>',
  inList: true,
};

class CategoryFieldEditable extends PureComponent {
  constructor(props) {
    super(props);

    const coaId = chartOfAccountsUtils.getCoaId(props.value, props.allowBlank); // allow blank
    const coaNode = this.getCoaNode(coaId, props);

    this.state = {
      tfValue: coaId === 'BLANK' ? '' : this.getDisplayName(coaNode),
      query: null,
      showMenu: false,
      placeholder: coaId === 'BLANK' ? props.placeholder : null,
      newCategoryClientId: null,
      inProgress: false,
    };
    this.currentHover = null;
    this.inputRef = null;
    this.fieldRef = undefined;
  }

  componentDidMount() {
    if (this.props.initialFocus && this.inputRef) {
      this.inputRef.focus();
    }
    if (this.props.openMenu) {
      this.setState({ showMenu: true });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.coaNodesById) {
      if (
        nextProps.value !== this.props.value ||
        nextProps.coaNodesById !== this.props.coaNodesById ||
        nextProps.longCats !== this.props.longCats
      ) {
        const coaId = chartOfAccountsUtils.getCoaId(nextProps.value, nextProps.allowBlank);
        const coaNode = this.getCoaNode(coaId, nextProps);
        // todo: if coaNode cannot be found, set it to uncategorized coaNode

        if (coaNode && coaNode.id !== 'NONE') {
          this.setState({ tfValue: this.getDisplayName(coaNode, nextProps) });
        } else if (coaNode?.id === 'NONE' || !nextProps.allowBlank) {
          this.setState({ tfValue: 'None' });
        } else if (nextProps.allowBlank) {
          this.setState({ tfValue: '' });
        }

        if (
          !this.props.value ||
          (this.inputRef && safeRefIn(nextProps, ['value', 'id']) !== safeRefIn(this.props, ['value', 'id']))
        ) {
          if (this.inputRef && this.props.autoFocus) {
            if (isTablet) {
              setTimeout(() => {
                if (this.inputRef) {
                  this.inputRef.focus();
                  this.inputRef.select();
                }
              }, 250);
            } else {
              this.inputRef.focus();
              this.inputRef.select();
            }
          }
        }
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { state, props } = this;
    if (state.tfValue !== '') {
      this.setState({
        placeholder: null,
      });
    }

    if (prevProps.categoriesById !== props.categoriesById && state.newCategoryClientId) {
      const newCategoryCreated = props.categoriesById.find(
        (category) => category.clientId === state.newCategoryClientId,
      );
      if (newCategoryCreated) {
        const newNode = props.coaNodesById.get(newCategoryCreated.id);
        const newCOA = newNode && newNode.coa;
        if (newCOA) {
          props.onChange(newCOA);
          this.setState({
            // eslint-disable-line react/no-did-update-set-state
            newCategoryClientId: null,
            placeholder: null,
            inProgress: false,
          });
        }
      }
    }
  }

  getCoaNode = (coaId, props) => {
    let ret = props.coaNodesById ? props.coaNodesById.get(coaId) : null;
    if (!ret && coaId === 'NONE') {
      return NONE_COA_NODE;
    }

    if (chartOfAccountsUtils.isTransferCoaNotFound(props.value)) {
      ret = props.coaNodesById.get(chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID);
    }
    return ret;
  };

  getDisplayName = (node, props = this.props) => {
    if (node) {
      return props.longCats ? node.fullPathName : node.name;
    }
    return ''; // was N/A, but making it just BLANK, which is invalid cat
  };

  handleRequestClose = () => {
    log.log('On Request Close...');
    this.setState({ showMenu: false });
    if (this.props.menuClose) {
      this.props.menuClose();
    }
  };

  menuOnChange = (item, meta) => {
    if (item) {
      if (!chartOfAccountsUtils.isCoaSettable(item.coa)) {
        return;
      }
      if (item.id === 'viewAll') {
        this.setState({ query: null, showMenu: true });
        return;
      }
      if (this.props.returnSelectionMethod) {
        // this.props.onChange({ coa: item.coa, selMethod: 'Menu' });
        this.props.onChange(item.coa, meta);
      } else {
        this.props.onChange(item.coa, meta);
      }
      this.handleRequestClose();
      this.setState({ query: null });
      if (this.props.menuClose) {
        this.props.menuClose();
      }
    }
  };

  renderCOAMenuNode = (coaNode, currentDepth = 1) => {
    const { stType, categoriesById, accountsById, depth = 4, filterFn, hasOpenBusiness, businessEnabled } = this.props;

    if (
      coaNode.coa?.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE &&
      coaNode.usageType === 'BUSINESS' &&
      businessEnabled &&
      !hasOpenBusiness
    ) {
      return {
        label: coaNode.name,
        value: coaNode,
        subMenu: [
          {
            label: 'add-business-prompt',
            disabled: true,
            customRender: (
              <ZeroStateView
                size='small'
                description='Create a business to use these categories'
                button={
                  <Button variant='tonal' size='small' href='/settings/businesses'>
                    Add Business
                  </Button>
                }
              />
            ),
          },
        ],
      };
    }

    if (currentDepth < depth && coaNode.children) {
      const menuItems = coaNode.children
        .map((childNode) =>
          !filterFn || filterFn(childNode.coa) ? this.renderCOAMenuNode(childNode, currentDepth + 1) : null,
        )
        .filter((x) => x !== null);

      if (menuItems.size > 0) {
        return { label: coaNode.name, value: coaNode, subMenu: menuItems };
      }
      // If filtered coaNode has no children and the type is purely a submenu we want to not show anything
      else if (
        coaNode.coa.type === chartOfAccountsTypes.CoaTypeEnum.DISPLAY_NODE ||
        coaNode.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE
      ) {
        return { nop: true };
      }
    }

    if (
      coaNode.inList &&
      (!filterFn || filterFn(coaNode.coa)) &&
      coaIncludedInList(coaNode.coa, categoriesById, accountsById, stType)
    ) {
      return { label: coaNode.name, value: coaNode };
    }
    return { nop: true };
  };

  buildMatchedString = (coaTypeIcon, match, indexOfMatch) => {
    const { theme } = this.props;
    const parentSplit = match.indexOf(':');
    if (parentSplit !== -1 && parentSplit < indexOfMatch) {
      // need to grey out parent
      return (
        <>
          {coaTypeIcon && <ListItemIcon sx={{ minWidth: '36px' }}>{coaTypeIcon}</ListItemIcon>}
          <ListItemText primaryTypographyProps={{ component: 'span' }}>
            <span style={{ color: theme.palette.text.secondary }}>{match.slice(0, parentSplit)}</span>
            <span>{match.slice(parentSplit, indexOfMatch)}</span>
            <span style={{ fontWeight: theme.typography.fontWeightMedium }}>
              {match.slice(indexOfMatch, indexOfMatch + this.state.query.length)}
            </span>
            <span>{match.slice(indexOfMatch + this.state.query.length)}</span>
          </ListItemText>
        </>
      );
    }

    const index = indexOfMatch === -1 ? 0 : indexOfMatch;
    const queryLength = index + this.state.query.replace(':', ' : ').length;

    // no parent so no greying out
    return (
      <>
        {coaTypeIcon && <ListItemIcon sx={{ minWidth: '36px' }}>{coaTypeIcon}</ListItemIcon>}
        <ListItemText primaryTypographyProps={{ component: 'span' }}>
          {match.slice(0, index)}
          <span style={{ fontWeight: theme.typography.fontWeightMedium }}>{match.slice(index, queryLength)}</span>
          {match.slice(queryLength)}
        </ListItemText>
      </>
    );
  };

  filterOptions = (options, query, usageSubHeaders = []) => {
    let items = [];
    const lowerCasedQuery = query.toLowerCase();

    options.forEach((option) => {
      if (!option.nop && !option.isSubheader && option.value && chartOfAccountsUtils.isCoaSettable(option.value.coa)) {
        const coaUsageType = option.value.usageType;
        const name = option.value.fullPathNameWithoutUsageType;

        if (coaUsageType === 'BUSINESS' && this.props.businessEnabled && !this.props.hasOpenBusiness) {
          return;
        }

        if (
          name.toLowerCase().indexOf(query.toLowerCase()) !== -1 &&
          (!this.props.filterFn || this.props.filterFn(option.value.coa))
        ) {
          if (this.props.enableUsageTypes && !this.props.coaUsageType && !usageSubHeaders.includes(coaUsageType)) {
            items.push({ label: coaUsageType, isSubheader: true });
            usageSubHeaders.push(coaUsageType);
          }
          // const CoaTypeIcon = this.props.enableUsageTypes ? (<CoaUsageTypeIcon size="small" usageType={coaUsageType} />) : null;
          const boldIndex = name.toLowerCase().indexOf(lowerCasedQuery);
          items.push({
            label: name,
            customRender: this.buildMatchedString(null, name, boldIndex),
            value: option.value,
          });
        } else if (option.value.fullPathName?.toLowerCase().includes(lowerCasedQuery)) {
          items.push({
            label: name,
            value: option.value,
          });
        }
      }
      if (option.subMenu) {
        items = items.concat(this.filterOptions(option.subMenu, query, usageSubHeaders));
      }
    });
    return items;
  };

  dropDownClick = () => {
    this.setState({ showMenu: !this.state.showMenu, query: null });
    if (this.inputRef) setTimeout(() => this.inputRef?.focus(), 50);
  };

  handleInputFocus = (event) => {
    event.target.focus();
    event.target.select(); // automatically highlights the input value
    // this.setState({ showMenu: true });
  };

  handleQueryChange = (event) => {
    this.setState({ query: event.target.value });
  };

  handleKeyDown = (event) => {
    if (this.props.tabOnEnter && event.key === 'Enter') {
      treatEnterAsTab(event);
    }
    if (this.state.showMenu && event.key !== 'Tab' && event.key !== 'Enter') {
      event.stopPropagation();
    } else if (event.key !== 'Shift' && event.key !== 'Escape' && event.key !== 'Enter' && event.key !== 'Tab') {
      this.setState({ showMenu: !this.state.showMenu });
    } else if (event.key === 'Enter' && (this.state.showMenu || this.state.query !== null)) {
      event.stopPropagation();
    } else if (event.key === 'Tab' && (this.state.showMenu || this.state.query?.length > 0) && this.currentHover) {
      if (this.currentHover.onSelect) {
        event.stopPropagation(); // if need to wait for category creation
        this.currentHover.onSelect();
      } else if (this.currentHover.value.coa) {
        this.props.onChange(this.currentHover.value.coa);
      }
    } else if (event.key === 'Escape') {
      this.handleRequestClose();
    }
  };

  handleCurrentHover = (current) => {
    if (this.currentHover !== current) this.currentHover = current;
  };

  handleNewCategoryClick = (newCatLabel, parentNode = null) => {
    const newCategoryClientId = uuidv4().toUpperCase();

    let parentId = '0';
    let usageType = 'PERSONAL';
    let type = this.props.recommendedCategoryType || categoriesTypes.CategoryTypeEnum.EXPENSE;

    if (parentNode?.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY_USAGE_TYPE) {
      usageType = parentNode.coa.id;
    }
    if (parentNode?.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
      const parentCategory = this.props.categoriesById.get(parentNode.coa.id);

      parentId = parentCategory.id;
      usageType = parentCategory?.usageType || null;
      type = parentCategory?.type ? parentCategory.type : categoriesTypes.CategoryTypeEnum.EXPENSE;
    }
    if (assert(newCatLabel, `category name is required (name = '${newCatLabel}') #bug-trap`)) {
      const newCategory = categoriesTypes.mkCategory({
        clientId: newCategoryClientId,
        parentId,
        name: newCatLabel.trim(),
        usageType,
        type,
      });
      this.props.dispatchCreateCategoryAction(newCategory);
      this.setState({
        newCategoryClientId,
        showMenu: false,
        query: null,
        placeholder: newCatLabel,
        inProgress: true,
      });
      setTimeout(() => {
        if (this.state.newCategoryClientId === newCategoryClientId && this.state.inProgress) {
          this.setState({
            inProgress: false,
          });
        }
      }, 8000);
    }
  };

  treeHasMainCategory = (name) =>
    this.props.coaTree.some((coaNode) => coaNode.name && coaNode.name.toLowerCase() === name.toLowerCase());

  // ##
  // ##  Render functions
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

  renderEndAdornment = () => {
    const { inProgress, showMenu, query } = this.state;
    const { theme, id, renderEndAdornment, disabled } = this.props;

    const flipIcon = showMenu || query !== null;
    if (!renderEndAdornment || disabled) {
      return null;
    }
    return (
      <div
        style={{
          minWidth: '36px',
          maxWidth: '36px',
        }}
      >
        {inProgress && ( // dont take up precious space for a 5% use case (on create cat)
          <CircularProgress size={16} style={{ visibility: inProgress ? 'visible' : 'hidden' }} />
        )}
        {this.props.error && <ErrorIcon style={{ color: theme.palette.amount.negative }} />}
        <QIconButton
          id={id ? `${id}-button` : 'category-button'}
          disabled={inProgress}
          style={{
            visibility: renderEndAdornment ? 'visible' : 'hidden',
            color: theme.palette.text.secondary,
            cursor: 'pointer',
            fontSize: '24px',
            opacity: flipIcon && 1,
          }}
          aria-label='Open Category List'
          focusRipple
          IconComponent={flipIcon ? ArrowDropUpRoundedIcon : ArrowDropDownRoundedIcon}
          onClick={this.dropDownClick}
          size='small-no-padding'
          TooltipProps={{ enterDelay: 10 }}
          tabIndex={-1}
        />
      </div>
    );
  };

  buildSubcategoryOfSubMenu = (coaNode, depth) => {
    // don't allow the new category to be nested under a category that is already at max depth
    const maxDepth = this.props.maxCategoryDepth ? this.props.maxCategoryDepth - 1 : 1;

    const menuNode = this.renderCOAMenuNode(coaNode);
    return {
      ...menuNode,
      subMenu:
        depth < maxDepth && coaNode.children
          ? coaNode.children.map((childCoaNode) => this.buildSubcategoryOfSubMenu(childCoaNode, depth + 1))
          : null,
      onSelect: () => this.handleNewCategoryClick(this.state.query, menuNode.value),
    };
  };

  buildCreateCategorySubmenuChoices = (coaTree, coaUsageNode) => [
    {
      label: 'Main Category',
      value: 'create-parent-category',
      onSelect: () => this.handleNewCategoryClick(this.state.query, coaUsageNode),
    },
    {
      label: 'Subcategory of',
      value: 'create-child-category',
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSelect: () => {},
      subMenu: coaTree?.map((coaNode) => this.buildSubcategoryOfSubMenu(coaNode, 1)),
    },
  ];

  buildCreateCategorySubmenu = () =>
    this.props.enableUsageTypes
      ? this.props.coaCategoryUsageTypeNodes.map((coaNode) => ({
          label: coaNode.name,
          value: coaNode.id,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSelect: () => {},
          subMenu: this.buildCreateCategorySubmenuChoices(coaNode.children, coaNode),
        }))
      : this.buildCreateCategorySubmenuChoices(this.props.coaCategoriesTree);

  render() {
    const { state } = this;
    const {
      allowNone,
      name,
      inputStyle,
      margin,
      numPopularCats,
      header,
      id,
      plain,
      autoFocus,
      fontSize,
      popularCats,
      filterFn,
      InputProps,
      label,
      textFieldProps,
      theme,
      width,
      textFieldVariant,
      sx,
      fullWidth,
    } = this.props;

    let menuItems = [];
    let selectedMenuItem;

    let popularCatsFiltered = filterFn ? popularCats.filter((x) => filterFn(x.coa)) : popularCats;
    if (this.props.businessEnabled && !this.props.hasOpenBusiness) {
      popularCatsFiltered = popularCatsFiltered.filter(
        (x) => this.props.categoriesById.get(x.coa?.id)?.usageType !== 'BUSINESS',
      );
    }

    const disableUnderlineObj =
      textFieldVariant === 'outlined' ? {} : { disableUnderline: this.props.disableUnderline };

    if (allowNone) {
      menuItems.push(this.renderCOAMenuNode(NONE_COA_NODE));
      menuItems.push({ nop: true, divider: true });
    }

    if (!this.state.query && popularCatsFiltered && popularCatsFiltered.size > 0) {
      if (numPopularCats > 0) {
        menuItems.push({
          isSubheader: true,
          label: 'Most Used Categories',
        });
      }
      popularCatsFiltered.slice(0, numPopularCats).forEach((catRecord, index) => {
        menuItems.push({
          label: chartOfAccountsSelectors.getCoaStringSelector(undefined, catRecord.coa, true),
          value: catRecord,
          subIndent: true,
          divider: index === popularCatsFiltered.slice(0, numPopularCats).size - 1,
        });
      });
    }

    // only need a sub-header to separate out categories if we are showing popular categories
    if (numPopularCats > 0) {
      menuItems = menuItems.concat([{ isSubheader: true, label: 'All Categories' }]);
    }
    menuItems = menuItems.concat(
      this.props.coaTree
        .map((coaNode) => (!filterFn || filterFn(coaNode.coa) ? this.renderCOAMenuNode(coaNode) : null))
        .toArray(),
    );
    menuItems = menuItems.filter((x) => x !== null);

    // No usage type nodes, Main Category, Subcategory Of (should support max-depth)
    // With usage-type nodes, By Usage Type, Subcaetgory Of (should support max-depth)

    if (this.state.query && this.state.query.trim()) {
      let addCatOption = this.props.createEnabled && !this.treeHasMainCategory(this.state.query);
      if (addCatOption) {
        addCatOption = {
          customRender: (
            <Typography>
              Create<span style={{ fontWeight: theme.typography.fontWeightMedium }}>{` "${this.state.query}"`}</span>
            </Typography>
          ),
          // create a new category when selected with tab or click, solves a datagrid SIMPL-27226
          onSelect: () => this.handleNewCategoryClick(this.state.query),
          value: 'create-category-bucket',
          label: 'create-category',
          subMenu: this.buildCreateCategorySubmenu(),
        };
      }

      menuItems = this.filterOptions(menuItems, this.state.query);

      if (menuItems && menuItems.length) {
        selectedMenuItem = menuItems.filter((item) => !item.isSubheader)[0];
      } else if (addCatOption) {
        selectedMenuItem = addCatOption;
      }

      if (addCatOption) {
        menuItems.unshift(addCatOption);
      }

      menuItems.push({
        label: 'See All Categories',
        value: 'see-all',
        onSelect: () => {
          this.setState({ query: null });
          if (this.inputRef) {
            this.inputRef.focus();
            this.inputRef.select();
          }
        },
      });
    }

    return (
      <div
        id={id ? `${id}-root` : 'category-root'}
        ref={(x) => {
          this.fieldRef = x;
        }}
        style={{ ...this.props.style, width }}
        className={this.props.classes?.root}
      >
        {plain ? (
          <InputBase
            sharedcomponentid='CategoryFieldEditable'
            data-testid='CategoryFieldEditable'
            classes={this.props.InputProps?.classes}
            margin='none'
            value={this.state.query !== null ? this.state.query : this.state.placeholder || this.state.tfValue || ''}
            onChange={this.handleQueryChange}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleInputFocus}
            onBlur={this.state.showMenu ? undefined : this.props.onBlur}
            endAdornment={this.renderEndAdornment()}
            variant='paragraphRegular'
            fullWidth={fullWidth}
            autoFocus={autoFocus}
          />
        ) : (
          <TextField
            sharedcomponentid='CategoryFieldEditable'
            data-testid='CategoryFieldEditable'
            disabled={state.inProgress || this.props.disabled}
            onClick={state.inProgress || this.props.disabled ? undefined : this.dropDownClick}
            {...textFieldProps}
            variant={textFieldVariant}
            className={this.props.className || ''}
            label={label}
            placeholder={this.state.placeholder}
            value={this.state.query !== null ? this.state.query : this.state.tfValue || ''}
            onChange={this.handleQueryChange}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleInputFocus}
            onBlur={this.state.showMenu ? undefined : this.props.onBlur}
            autoFocus={autoFocus}
            style={{ width: '100%' }}
            error={this.props.error}
            helperText={this.props.helperText}
            name={name}
            margin={margin}
            autoComplete='off'
            InputProps={{
              ...InputProps,
              ...disableUnderlineObj,
              id: id ? `${id}-input` : 'category-input',
              endAdornment: (
                <div
                  style={{
                    minWidth: '36px',
                    maxWidth: '36px',
                  }}
                >
                  {this.renderEndAdornment()}
                </div>
              ),
              sx: {
                paddingTop: 0,
                paddingBottom: 0,
                height: textFieldVariant === 'filled' ? 'auto' : '',
                lineHeight: '22px',
                fontSize,
                '& input': {
                  overFlow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                color:
                  (state.query && state.query !== state.tfValue) ||
                  (state.placeholder && state.placeholder !== state.tfValue)
                    ? theme.palette.text.secondary
                    : undefined,
                ...(InputProps ? InputProps.style : {}),
                ...sx,
              },
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              className: inputStyle || '',
              style: { height: 23 },
            }}
            inputRef={(x) => {
              if (this.props.inputRef) {
                this.props.inputRef(x);
              }
              this.inputRef = x;
            }}
            fullWidth={fullWidth}
          />
        )}

        <div style={{ margin: 'auto' }}>
          <NestedQMenu
            saveMenuSelectionOnTab={this.props.saveOnTab}
            anchorEl={this.fieldRef}
            header={header}
            name='catmenu'
            options={menuItems}
            selectedOption={selectedMenuItem}
            onChange={this.menuOnChange}
            open={this.state.showMenu || this.state.query !== null}
            onClose={() => {
              this.setState({ query: null });
              this.handleRequestClose();
              if (this.props.blurOnMenuClose) this.props.onBlur({ relatedTarget: 'categoryfield' });
            }}
            currentHover={this.handleCurrentHover}
            tabOnEnter={this.props.tabOnEnter}
          />
        </div>
      </div>
    );
  }
}

CategoryFieldEditable.propTypes = {
  theme: PropTypes.object,
  style: PropTypes.object,
  // props from parent
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textFieldVariant: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.object,
  initialFocus: PropTypes.bool,
  name: PropTypes.string,
  id: PropTypes.string,
  returnSelectionMethod: PropTypes.bool,
  longCats: PropTypes.bool,
  depth: PropTypes.number,
  allowBlank: PropTypes.bool,
  allowNone: PropTypes.bool,
  tooltip: PropTypes.bool, // show a tooltip on static value?
  disableUnderline: PropTypes.bool,
  autoFocus: PropTypes.bool,
  history: PropTypes.object,
  fontSize: PropTypes.string,
  label: PropTypes.string,
  margin: PropTypes.string,
  helperText: PropTypes.string,
  inputRef: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  createEnabled: PropTypes.bool,
  recommendedCategoryType: PropTypes.string,
  filterFn: PropTypes.func,
  inputStyle: PropTypes.string,
  stType: PropTypes.string,
  InputProps: PropTypes.object,
  textFieldProps: PropTypes.object,
  className: PropTypes.string,
  numPopularCats: PropTypes.number,
  openMenu: PropTypes.bool,
  blurOnMenuClose: PropTypes.bool,
  classes: PropTypes.object,
  header: PropTypes.object,
  menuClose: PropTypes.func,
  plain: PropTypes.bool,
  tabOnEnter: PropTypes.bool,
  renderEndAdornment: PropTypes.bool,
  sx: PropTypes.object,
  placeholder: PropTypes.string,

  transferOnly: PropTypes.bool,
  coaUsageType: PropTypes.string,

  // props from redux
  coaTree: PropTypes.object,
  coaCategoriesTree: PropTypes.object,
  coaCategoryUsageTypeNodes: PropTypes.object,
  coaNodesById: PropTypes.object,
  enableUsageTypes: PropTypes.bool,
  hasOpenBusiness: PropTypes.bool,
  businessEnabled: PropTypes.bool,
  categoriesById: PropTypes.object,
  accountsById: PropTypes.object,
  popularCats: PropTypes.object,
  dispatchCreateCategoryAction: PropTypes.func,
  context: PropTypes.string,
  maxCategoryDepth: PropTypes.number,
  saveOnTab: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
};

CategoryFieldEditable.defaultProps = {
  transferOnly: false,
  numPopularCats: 5,
  width: '100%',
  textFieldVariant: 'standard',
  context: '',
  plain: false,
  renderEndAdornment: true,
  sx: {},
  disabled: false,
};

function mapStateToProps(state, ownProps) {
  let coaTree;
  if (ownProps.coaUsageType) {
    coaTree = chartOfAccountsSelectors.getChartOfAccountsTreeByUsageType(state).get(ownProps.coaUsageType);
  } else if (ownProps.transferOnly) {
    coaTree = chartOfAccountsSelectors.getTransferCOATree(state);
  } else {
    coaTree = chartOfAccountsSelectors.getChartOfAccountsTree(state, ownProps);
  }
  return {
    coaTree,
    coaCategoriesTree: chartOfAccountsSelectors.getChartOfAccountCategoriesTree(state),
    coaCategoryUsageTypeNodes: chartOfAccountsSelectors.getChartOfAccountCategoryUsageTypeNodes(state),
    enableUsageTypes: categoriesSelectors.enableCategoryUsageTypes(state),
    hasOpenBusiness: businessSelectors.getOpenBusinesses(state)?.size > 0,
    businessEnabled: entitlementsSelectors.getBusinessEnabled(state),
    categoriesById: categoriesSelectors.getCategoriesById(state),
    accountsById: accountsSelectors.getAccountsById(state),
    popularCats: transactionsSelectors.getMostPopularCatsByAccountId(state),
    maxCategoryDepth: Number(featureFlagsSelectors.getFeatureFlags(state).get('maxCategoryDepth')),
    saveOnTab: featureFlagsSelectors.getFeatureFlags(state).get('categorySelectOnTab'),
    depth: ownProps.depth ? ownProps.depth : entitlementsSelectors.getBusinessEnabled(state) ? 4 : 3,
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatchCreateCategoryAction: (data) =>
      dispatch(
        categoriesActions.createCategory(data, {
          undo: { userMessage: 'Category created.' },
          context: ownProps.context,
        }),
      ),
  };
}

// note: order-matters - props flow from top to bottom
export default compose(withTheme)(connect(mapStateToProps, mapDispatchToProps)(CategoryFieldEditable));
